import { MainContent } from '../shared-mui-components/MainContent.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import {
    RequestStatus,
    ServerRequestState,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { LoadingSpinnerPresets } from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner.tsx';
import {
    EmptyResults,
    FetchError,
    ResultLimitAlert,
} from '../shared-mui-components/ListMessages.tsx';
import {
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import { DateTime } from 'luxon';
import { HeaderWithButtons } from '../shared-mui-components/HeaderWithButtons.tsx';
import { FiltersBox } from '../shared-mui-components/filter/FiltersBox.tsx';
import { useProductFilterConfiguration } from '../shared-mui-components/filter/OperatorFilterConfiguration.tsx';
import { Message } from 'dg-web-shared/lib/Localized.ts';
import {
    OperatorProductStatus,
    OperatorProductStatusIcon,
} from './OperatorProductState.tsx';
import { CloudDownload, LocalParking } from '@mui/icons-material';
import { useLanguage } from 'dg-web-shared/lib/Localization.tsx';
import {
    Outlet,
    useLocation,
    useNavigate,
    useOutletContext,
} from 'react-router';
import { OperatorProductDetailModal } from './OperatorProductDetailModal.tsx';
import React from 'react';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { urlWithOperatorExternalId } from '../utils/Utils.ts';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';

export function OperatorProducts() {
    return <ProductLayout />;
}

interface ProductsResultLine {
    contractExternalId: string;
    status: OperatorProductStatus;
    name: Message;
    validFrom: string;
    validTo: string | null;
    productOwnedBy: string | null;
    createdAt: string;
}

export interface ProductSearchResult {
    eof: boolean;
    results: ProductsResultLine[];
}

function ProductLayout() {
    const { operatorExternalId } = useOperatorContext();
    const { search } = useLocation();
    const { language } = useLanguage();
    const paramsWithLanguage = React.useMemo(() => {
        const params = new URLSearchParams(search);
        params.append('language', language);
        return params;
    }, [search, language]);

    const {
        filterConfig,
        activeFilters,
        listState,
        refetchList,
        searchText,
        filterConfigState,
    } = useProductFilterConfiguration();

    return (
        <MainContent>
            <HeaderWithButtons
                title={
                    <Localized
                        de="Produkte"
                        fr="Produits"
                        it="Prodotti"
                        en="Products"
                    />
                }
                icon={LocalParking}
                headerButtons={
                    listState.status === RequestStatus.SUCCESS &&
                    listState.data.results.length > 0 ? (
                        <Button
                            variant="outlined"
                            startIcon={<CloudDownload />}
                            href={urlWithOperatorExternalId(
                                `/ui-api/operator-account/product/export?${paramsWithLanguage.toString()}`,
                                operatorExternalId,
                            )}
                        >
                            <Localized
                                de="Exportieren"
                                fr="Exporter"
                                it="Esporta"
                                en="Export"
                            />
                        </Button>
                    ) : undefined
                }
            />
            <FiltersBox
                searchText={searchText}
                activeFilters={activeFilters}
                filterConfig={filterConfig}
                requestState={listState}
                refetch={refetchList}
                pending={filterConfigState.status === RequestStatus.PENDING}
                tooltip={<ProductsSearchTooltip />}
            />
            <OperatorProductsList
                listState={listState}
                refetchList={refetchList}
            />
            <OperatorProductListOutlet context={{ refetchList }} />
        </MainContent>
    );
}

type OperatorProductListOutletContext = {
    refetchList: () => void;
};

export function useOperatorProductListContext() {
    return useOutletContext<OperatorProductListOutletContext>();
}

export function OperatorProductListOutlet({
    context,
}: {
    context: OperatorProductListOutletContext;
}) {
    return <Outlet context={context} />;
}

function OperatorProductsList({
    listState,
    refetchList,
}: {
    listState: ServerRequestState<ProductSearchResult, object>;
    refetchList: () => void;
}) {
    const { search } = useLocation();
    const navigate = useNavigate();
    return (
        <OperatorAsyncLoadedSection
            requestState={listState}
            pendingLoaderPreset={LoadingSpinnerPresets.FillAllSpaceAndCenter}
            render={results => {
                const isEmpty = results.results.length === 0;
                if (isEmpty) {
                    return <EmptyResults />;
                }

                return (
                    <>
                        <TableContainer>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <Localized
                                                de="Status"
                                                fr="État"
                                                it="Stato"
                                                en="State"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="Bezeichnung"
                                                fr="Désignation"
                                                it="Denominazione"
                                                en="Denomination"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="Beginn"
                                                fr="Début"
                                                it="Inizio"
                                                en="Start"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="Ende"
                                                fr="Fin"
                                                it="Fine"
                                                en="End"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="Benutzer"
                                                fr="Utilisateur"
                                                it="Utente"
                                                en="User"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Localized
                                                de="Erstellt am"
                                                fr="Créé le"
                                                it="Creato il"
                                                en="Created at"
                                            />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {results.results.map(product => (
                                        <TableRow
                                            key={product.contractExternalId}
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() =>
                                                navigate(
                                                    `${product.contractExternalId}/detail${search}`,
                                                )
                                            }
                                        >
                                            <TableCell>
                                                <OperatorProductStatusIcon
                                                    status={product.status}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Localized {...product.name} />
                                            </TableCell>
                                            <TableCell>
                                                {Formatter.dayMonthYearHourMinute(
                                                    DateTime.fromISO(
                                                        product.validFrom,
                                                    ),
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {product.validTo
                                                    ? Formatter.dayMonthYearHourMinute(
                                                          DateTime.fromISO(
                                                              product.validTo,
                                                          ),
                                                      )
                                                    : '-'}
                                            </TableCell>
                                            <TableCell>
                                                {product.productOwnedBy ??
                                                    EmDash}
                                            </TableCell>
                                            <TableCell>
                                                {Formatter.dayMonthYearHourMinute(
                                                    DateTime.fromISO(
                                                        product.createdAt,
                                                    ),
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <ResultLimitAlert
                                eof={results.eof}
                                numberOfResults={results.results.length}
                                hasExport={true}
                            />
                        </TableContainer>
                    </>
                );
            }}
            renderError={() => <FetchError refetchList={refetchList} />}
        />
    );
}

export function OperatorProductDetail() {
    const { refetchList } = useOperatorProductListContext();
    const { search } = useLocation();

    return (
        <OperatorProductDetailModal
            backUrl={`..${search}`}
            onProductChange={refetchList}
        />
    );
}

export function ProductsSearchTooltip() {
    return (
        <Box
            sx={{
                fontSize: 14,
                padding: '18px 18px 5px 18px',
                width: '300px',
            }}
        >
            <Localized
                de={
                    <>
                        Verfügbare Suchkriterien:
                        <ul>
                            <li>Produkt-ID</li>
                            <li>QR-Code Nummer</li>
                            <li>Interne Bemerkungen</li>
                        </ul>
                    </>
                }
                fr={
                    <>
                        Critères de recherche disponibles:
                        <ul>
                            <li>ID produit</li>
                            <li>numéro de code QR</li>
                            <li>remarques internes</li>
                        </ul>
                    </>
                }
                it={
                    <>
                        Criteri di ricerca disponibili:
                        <ul>
                            <li>ID prodotto</li>
                            <li>numero del codice QR</li>
                            <li>osservazioni interne</li>
                        </ul>
                    </>
                }
                en={
                    <>
                        Available search criteria:
                        <ul>
                            <li>product ID</li>
                            <li>QR code number</li>
                            <li>internal remarks</li>
                        </ul>
                    </>
                }
            />
        </Box>
    );
}
